// Rechnung der Drucker-Seite (/werkstatt/drucker): Typen der Wire-Form
// (internal/bambu) und alles, was aus dem Stand Text, Ton und Lage macht.
// Rein und mit der Uhr als Parameter -- getestet in drucker.test.mjs.
// SoT: docs/drucker.md.

import type { LageTeil } from "../lage";
import type { Laeuft } from "../laeuft";

export interface Fach {
  /** "A1" … "D4", "Extern" */
  platz: string;
  material?: string;
  name?: string;
  /** #RRGGBB, die echte Farbe der Spule */
  farbe?: string;
  /** Rest in %, fehlt bei fremden Spulen */
  rest?: number;
  aktiv: boolean;
  leer: boolean;
  duese_min?: number;
  duese_max?: number;
  gewicht?: number;
  durchmesser?: number;
  /** Filament-Profil des Fachs (tray_info_idx, z. B. GFB98) */
  kennung?: string;
  /** Bambu-Spule mit RFID: liest der Drucker selbst */
  rfid?: boolean;
  /** so viel ist ungefähr noch drauf, in g (internal/bambu/spulen.go);
   *  fehlt = unbekannt (fremde Spule ohne Angabe) */
  rest_g?: number;
  rest_quelle?: "rfid" | "neu" | "geschaetzt" | "gewogen";
  /** die Angabe zu einer fremden Spule mit den letzten Abbuchungen */
  spule?: Spule;
  /** zuletzt gewogene leere Spule dieses Profils, in g */
  leerspule_g?: number;
}

/** Angabe zu einer fremden Spule (Tim 20.10.2026: Restmenge im Homelab). */
export interface Spule {
  platz: string;
  start_g: number;
  rest_g: number;
  quelle: "neu" | "geschaetzt" | "gewogen";
  seit: string;
  wer?: string;
  buchungen?: { zeit: string; datei: string; gramm: number; anteilig?: boolean }[];
}

/** Ab hier gilt eine fremde Spule als fast leer (g). */
export const FAST_LEER_G = 50;

/** Rest in % für den Balken: RFID vom Drucker, sonst Rest/Start der Angabe. */
export function restAnteil(f: Fach): number | null {
  if (f.rest_quelle === "rfid" || (f.rest != null && f.rest_g == null)) return f.rest ?? null;
  if (f.spule && f.spule.start_g > 0) return Math.max(0, Math.min(100, Math.round((f.spule.rest_g / f.spule.start_g) * 100)));
  return null;
}

/** Text der Restmenge: „ca. 260 g" bzw. „32 % · 320 g" (RFID). */
export function restText(f: Fach): string {
  if (f.rest_quelle === "rfid") return f.rest_g != null ? `${f.rest} % · ${f.rest_g} g` : `${f.rest} %`;
  if (f.rest_g != null) return `ca. ${f.rest_g} g`;
  if (f.rest != null) return `${f.rest} %`;
  return "";
}

/** Rest kurz, für die Spulen-Kachel: „320 g" (RFID), „≈ 260 g" (Angabe),
 *  „32 %" (nur Prozent), „? g" (fremde Spule ohne Angabe), "" (leer). */
export function restKurz(f: Fach): string {
  if (f.leer) return "";
  if (f.rest_g != null) return f.rest_quelle === "rfid" ? `${Math.round(f.rest_g)} g` : `≈ ${Math.round(f.rest_g)} g`;
  if (f.rest != null) return `${f.rest} %`;
  return "? g";
}

/** Der Platz auf der Kachel: „Extern" heißt dort „Ext". */
export function platzKurz(platz: string): string {
  return platz === "Extern" ? "Ext" : platz;
}

/** Fast leer: eine fremde Spule mit Angabe nach Gramm (≤ 50 g), eine
 *  Bambu-Spule nach Prozent (≤ 10 %). */
export function fastLeer(f: Fach): boolean {
  if (f.leer) return false;
  if (f.rest_g != null && f.rest_quelle !== "rfid") return f.rest_g <= FAST_LEER_G;
  return f.rest != null && f.rest <= FAST_LEER;
}

/** Woher der Rest kommt, für den Tooltip. */
export function restHerkunft(f: Fach): string {
  if (f.rest_quelle === "rfid") return "vom Drucker (RFID der Bambu-Spule)";
  const s = f.spule;
  if (!s) return "";
  const art = s.quelle === "neu" ? `neu eingelegt mit ${Math.round(s.start_g)} g` : s.quelle === "gewogen" ? `gewogen: ${Math.round(s.start_g)} g` : `geschätzt: ${Math.round(s.start_g)} g`;
  const b = s.buchungen?.[0];
  const zuletzt = b ? ` · zuletzt −${b.gramm} g${b.anteilig ? " (abgebrochen, anteilig)" : ""} für ${b.datei}` : "";
  return `${art}${zuletzt} · danach jeder Druck abgezogen`;
}

/** Fremde Spule ohne Angabe: Rest unbekannt, aber angebbar. */
export const restOffen = (f: Fach) => !f.leer && !f.rfid && f.rest_g == null;

export interface AMS {
  id: number;
  feuchte_prozent?: number;
  temperatur?: number;
  faecher: Fach[];
}

export interface HMS {
  code: string;
  stufe: "fatal" | "ernst" | "normal" | "hinweis";
  modul?: string;
  link: string;
}

export interface Wache {
  name: string;
  an: boolean;
}

export type DruckZustand = "bereit" | "vorbereiten" | "druckt" | "pausiert" | "fertig" | "gescheitert" | "abgebrochen";

export interface Druck {
  zustand: DruckZustand;
  /** Fächer des laufenden Auftrags (Startbefehl; sonst beobachtet) --
   *  sofort da, nicht erst mit der Vorschau */
  auftrag_faecher?: string[];
  schritt: number;
  schritt_text?: string;
  pause_vom_drucker?: boolean;
  datei?: string;
  fortschritt: number;
  rest_min: number;
  ende?: string;
  beginn?: string;
  /** Start nicht gesehen, aus Fortschritt und Restzeit gerechnet (≈) */
  beginn_geschaetzt?: boolean;
  schicht: number;
  schichten: number;
  fehlercode?: string;
  duese: number;
  duese_ziel: number;
  bett: number;
  bett_ziel: number;
  kammer?: number;
  luefter_bauteil?: number;
  luefter_hilfs?: number;
  luefter_kammer?: number;
  tempo?: "leise" | "standard" | "sport" | "wahnsinn";
  tempo_prozent?: number;
  licht?: boolean;
  /** AMS-Ersatzspule (Auto Refill): leeres Fach → gleiche Spule nimmt weiter */
  ersatzspule?: boolean;
  /** geplante Schritte des Auftrags (stg), mit Namen vom Server */
  ablauf: { nr: number; text: string }[];
  duesendurchmesser?: string;
  duesentyp?: string;
  wlan_dbm?: number;
  sd_karte?: boolean;
  zeitraffer?: boolean;
  aufnahme?: boolean;
  erkennung: Wache[];
  update?: string;
  ams: AMS[];
  extern?: Fach;
  hms: HMS[];
}

export interface Filament {
  material: string;
  farbe?: string;
  meter: number;
  gramm: number;
  /** aus welchem Fach ("A1", "Extern") -- nur im laufenden Auftrag
   *  (internal/bambu dienst.go vorschauMitFaechern) */
  platz?: string;
}

export interface Vorschau {
  datei: string;
  platte: number;
  bild?: string;
  schaetzung_s?: number;
  gewicht_g?: number;
  objekte: string[];
  filamente: Filament[];
  /** Draufsicht der Platte vorhanden (Bild-Schlüssel + "-oben") */
  oben?: boolean;
  schichthoehe_mm?: number;
}

export type Verbindung =
  | "nicht_gefunden"
  | "kein_zugangscode"
  | "verbindet"
  | "verbunden"
  | "aus"
  | "abgelehnt"
  | "zertifikat"
  | "fehler";

export interface Stand {
  seriennummer?: string;
  name: string;
  geraetename?: string;
  modell_code?: string;
  host?: string;
  firmware?: string;
  nur_lan: boolean;
  verbindung: Verbindung;
  verbindung_seit: string;
  verbindung_text?: string;
  zugangscode_gesetzt: boolean;
  zuletzt_gesehen?: string;
  letzte_meldung?: string;
  kamera: boolean;
  druck?: Druck;
  vorschau?: Vorschau;
}

export interface Auftrag {
  id: number;
  ergebnis: "fertig" | "abgebrochen" | "gescheitert";
  datei: string;
  beginn: string;
  beginn_geschaetzt?: boolean;
  ende: string;
  dauer_s: number;
  fortschritt: number;
  schichten?: number;
  fehlercode?: string;
  schritt_text?: string;
  vorschau?: Vorschau;
}

export type Ton = "ok" | "warn" | "danger" | "accent" | "leise";

// ── Zahlen und Zeiten ──────────────────────────────────────────────

/** 63 -> "1 h 03 min", 47 -> "47 min", 0 -> "0 min". */
export function minutenText(min: number): string {
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

/** Sekunden als Dauer in Worten (wie minutenText, darunter Sekunden). */
export function dauerText(s: number): string {
  if (s < 60) return `${Math.max(0, Math.round(s))} s`;
  return minutenText(s / 60);
}

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Uhrzeit eines Zeitpunkts, mit Tag, wenn es nicht heute ist:
 *  "16:40", "morgen 02:10", "Do 09:15". */
export function uhrText(iso: string | undefined, jetzt: Date): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const tage = Math.round((tag(d) - tag(jetzt)) / 86_400_000);
  if (tage === 0) return hm;
  if (tage === 1) return `morgen ${hm}`;
  if (tage === -1) return `gestern ${hm}`;
  if (Math.abs(tage) < 7) return `${WOCHENTAG[d.getDay()]} ${hm}`;
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}. ${hm}`;
}

/** 219.9 -> "220 °C" (am Drucker zaehlen ganze Grad). */
export function gradText(v: number | undefined): string {
  return v == null ? "—" : `${Math.round(v)} °C`;
}

/** Komma statt Punkt, eine Stelle: 24.6 -> "24,6". */
export function komma(v: number, stellen = 1): string {
  return v.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

// ── Zustand ────────────────────────────────────────────────────────

export const ZUSTAND_TEXT: Record<DruckZustand, string> = {
  bereit: "bereit",
  vorbereiten: "bereitet vor",
  druckt: "druckt",
  pausiert: "pausiert",
  fertig: "fertig",
  gescheitert: "gescheitert",
  abgebrochen: "abgebrochen",
};

export const TEMPO_TEXT: Record<string, string> = {
  leise: "Leise",
  standard: "Standard",
  sport: "Sport",
  wahnsinn: "Wahnsinn",
};

/** Zustand des Druckers als Wort und Ton (StatusBadge). Läuft = Akzent,
 *  ein Handgriff = gelb, ein Fehlschlag = rot; „bereit" und „fertig"
 *  sind in Ordnung, „aus" und eine Pause von Hand haben kein Urteil. */
export function zustand(s: Stand | undefined): { text: string; ton: Ton } {
  if (!s) return { text: "lädt …", ton: "leise" };
  switch (s.verbindung) {
    case "nicht_gefunden":
      return { text: "nicht gefunden", ton: "leise" };
    case "kein_zugangscode":
      return { text: "Zugangscode fehlt", ton: "warn" };
    case "abgelehnt":
      return { text: "Zugangscode abgelehnt", ton: "warn" };
    case "zertifikat":
      return { text: "Zertifikat passt nicht", ton: "danger" };
    case "aus":
      return { text: "aus", ton: "leise" };
    case "fehler":
      return { text: "Fehler", ton: "warn" };
  }
  const d = s.druck;
  if (!d) return { text: "verbindet …", ton: "leise" };
  switch (d.zustand) {
    case "vorbereiten":
    case "druckt":
      return { text: ZUSTAND_TEXT[d.zustand], ton: "accent" };
    case "pausiert":
      return d.pause_vom_drucker
        ? { text: `angehalten${d.schritt_text ? ": " + d.schritt_text : ""}`, ton: "warn" }
        : { text: "pausiert", ton: "leise" };
    case "gescheitert":
      return { text: "gescheitert", ton: "danger" };
    case "abgebrochen":
      return { text: "abgebrochen", ton: "leise" };
    default:
      return { text: ZUSTAND_TEXT[d.zustand] ?? d.zustand, ton: "ok" };
  }
}

export function istAktiv(d: Druck | undefined): boolean {
  return !!d && (d.zustand === "vorbereiten" || d.zustand === "druckt" || d.zustand === "pausiert");
}

/** Druckerfehler, die zählen (Hinweise nicht). */
export function fehler(d: Druck | undefined): HMS[] {
  return (d?.hms ?? []).filter((h) => h.stufe !== "hinweis");
}

export function hmsTon(h: HMS): Ton {
  return h.stufe === "fatal" || h.stufe === "ernst" ? "danger" : h.stufe === "normal" ? "warn" : "leise";
}

/** Lage-Satz der Sektion „Druck": Ausnahme vorn, sonst ein Wort. */
export function druckLage(s: Stand | undefined, jetzt: Date): LageTeil[] {
  if (!s) return [];
  const z = zustand(s);
  const d = s.druck;
  const teile: LageTeil[] = [];
  const f = fehler(d);
  if (f.length) {
    const schlimm = f.some((h) => hmsTon(h) === "danger");
    teile.push({ text: f.length === 1 ? "1 Druckerfehler" : `${f.length} Druckerfehler`, ton: schlimm ? "danger" : "warn" });
  }
  if (!d) {
    const teil: LageTeil = { text: z.text };
    if (z.ton !== "leise" && z.ton !== "accent") teil.ton = z.ton;
    teile.push(teil);
    if (s.verbindung === "aus" && s.zuletzt_gesehen) {
      teile.push({ text: `zuletzt gehört ${vorText(s.zuletzt_gesehen, jetzt)}` });
    }
    return teile;
  }
  switch (d.zustand) {
    case "vorbereiten":
    case "druckt": {
      teile.push({ text: `${z.text} · ${d.fortschritt} %`, ton: "accent" });
      if (d.ende) teile.push({ text: `fertig gegen ${uhrText(d.ende, jetzt)}` });
      else if (d.rest_min > 0) teile.push({ text: `noch ${minutenText(d.rest_min)}` });
      break;
    }
    case "pausiert":
      teile.push({ text: `${z.text} · ${d.fortschritt} %`, ton: d.pause_vom_drucker ? "warn" : undefined });
      break;
    case "gescheitert":
      teile.push({ text: d.fehlercode ? `gescheitert · Fehler ${d.fehlercode}` : "gescheitert", ton: "danger" });
      break;
    case "fertig":
      teile.push({ text: d.datei ? `fertig · ${d.datei}` : "fertig", ton: "ok" });
      break;
    case "abgebrochen":
      teile.push({ text: d.datei ? `abgebrochen · ${d.datei}` : "abgebrochen" });
      break;
    default:
      teile.push({ text: "bereit", ton: "ok" });
  }
  return teile;
}

/** "vor 3 min" -- grob, für Lage-Sätze. */
export function vorText(iso: string, jetzt: Date): string {
  const s = Math.max(0, (jetzt.getTime() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "gerade eben";
  if (s < 3600) return `vor ${Math.round(s / 60)} min`;
  if (s < 86_400) return `vor ${Math.round(s / 3600)} h`;
  return `vor ${Math.round(s / 86_400)} d`;
}

// ── AMS ────────────────────────────────────────────────────────────

/** Ab hier gilt eine Spule als fast leer. */
export const FAST_LEER = 10;

/** Alle Spulenplätze in Reihenfolge, die externe zuletzt. */
export function alleFaecher(d: Druck | undefined): Fach[] {
  if (!d) return [];
  const out = d.ams.flatMap((a) => a.faecher);
  if (d.extern) out.push(d.extern);
  return out;
}

/** Lage-Satz des AMS: welches Fach druckt, was fast leer ist. Gelb nur,
 *  wenn das Fach, aus dem gerade gedruckt wird, fast leer ist -- eine
 *  fast leere Spule im Regal ist kein Handgriff. */
export function amsLage(d: Druck | undefined): LageTeil[] {
  const faecher = alleFaecher(d);
  if (!d || faecher.length === 0) return [];
  const teile: LageTeil[] = [];
  const aktiv = faecher.find((f) => f.aktiv && !f.leer);
  const laeuft = istAktiv(d);
  for (const f of faecher) {
    if (!fastLeer(f)) continue;
    // Fremde Spule mit Angabe: nach Gramm; Bambu-Spule: nach Prozent.
    const text = f.rest_g != null && f.rest_quelle !== "rfid" ? `${f.platz} fast leer (ca. ${f.rest_g} g)` : `${f.platz} fast leer (${f.rest} %)`;
    teile.push({ text, ton: f === aktiv && laeuft ? "warn" : undefined });
  }
  if (aktiv) teile.push({ text: `${aktiv.platz} ${laeuft ? "druckt" : "geladen"} · ${aktiv.material ?? ""}`.trim(), ton: laeuft ? "accent" : undefined });
  const belegt = faecher.filter((f) => !f.leer).length;
  if (!aktiv && teile.length === 0) teile.push({ text: `${belegt} von ${faecher.length} belegt` });
  return teile;
}

// ── Ablauf als Leitung ─────────────────────────────────────────────

/** Die geplanten Schritte als Halte; der laufende ist aktiv, die davor
 *  erledigt. Gedruckt wird immer zuletzt (Schritt 0). */
export function ablaufHalte(
  d: Druck | undefined,
): { key: string; etikett: string; zustand: "fertig" | "aktiv" | "wartet" | "da" }[] {
  if (!d) return [];
  const schritte = d.ablauf.filter((s) => s.nr !== 0);
  schritte.push({ nr: 0, text: "drucken" });
  const fertig = d.zustand === "fertig";
  const idx = schritte.findIndex((s) => s.nr === d.schritt);
  return schritte.map((s, i) => ({
    key: `${i}-${s.nr}`,
    etikett: s.nr === 0 ? "drucken" : s.text,
    zustand: fertig ? "da" : idx < 0 ? (i === schritte.length - 1 && d.zustand === "druckt" ? "aktiv" : "wartet") : i < idx ? "fertig" : i === idx ? "aktiv" : "wartet",
  }));
}

// ── Verlauf ────────────────────────────────────────────────────────

export function verlaufLage(a: Auftrag[], jetzt: Date): LageTeil[] {
  if (a.length === 0) return [{ text: "noch keine Drucke" }];
  const teile: LageTeil[] = [];
  const woche = a.filter((x) => jetzt.getTime() - new Date(x.ende).getTime() < 7 * 86_400_000);
  const gescheitert = woche.filter((x) => x.ergebnis === "gescheitert").length;
  if (gescheitert) teile.push({ text: `${gescheitert} gescheitert diese Woche`, ton: "warn" });
  const fertig = woche.filter((x) => x.ergebnis === "fertig");
  if (fertig.length) {
    const stunden = fertig.reduce((s, x) => s + x.dauer_s, 0) / 3600;
    teile.push({ text: `${fertig.length} fertig in 7 Tagen · ${komma(stunden)} h gedruckt` });
  }
  teile.push({ text: `zuletzt ${vorText(a[0].ende, jetzt)}` });
  return teile;
}

export const ERGEBNIS: Record<Auftrag["ergebnis"], { text: string; ton: Ton }> = {
  fertig: { text: "fertig", ton: "ok" },
  abgebrochen: { text: "abgebrochen", ton: "leise" },
  gescheitert: { text: "gescheitert", ton: "danger" },
};

// ── Läuft gerade (Statusleiste, Baum) ──────────────────────────────

/** Der Eintrag für „läuft gerade", solange ein Auftrag aktiv ist. Eine
 *  Pause durch den Drucker ist ein Handgriff (gelb), eine von Hand leise. */
export function druckerLaeuft(s: Stand | undefined): Laeuft | undefined {
  const d = s?.druck;
  if (!s || !d || !istAktiv(d)) return undefined;
  const basis = { id: "drucker", name: s.name, pfad: "/werkstatt/drucker" };
  const datei = d.datei ? ` — ${d.datei}` : "";
  if (d.zustand === "pausiert") {
    const was = d.pause_vom_drucker ? `angehalten${d.schritt_text ? ": " + d.schritt_text : ""}` : "pausiert";
    return { ...basis, was, fortschritt: d.pause_vom_drucker ? undefined : d.fortschritt, ton: d.pause_vom_drucker ? "warn" : "leise", titel: `${s.name} ${was} bei ${d.fortschritt} %${datei}` };
  }
  const was = ZUSTAND_TEXT[d.zustand];
  return {
    ...basis,
    was,
    fortschritt: d.fortschritt,
    bis: d.ende,
    ton: "laeuft",
    titel: `${s.name} ${was} ${d.fortschritt} %${d.rest_min > 0 ? ", noch " + minutenText(d.rest_min) : ""}${datei}`,
  };
}

/** Fächer, die sich als Ersatzspule vertreten (AMS Auto Refill): gleiches
 *  Filament-Profil und gleiche Farbe, nur im AMS (nicht die Spule hinten).
 *  Ergebnis z. B. ["A3 ↔ A4"]. */
export function ersatzPaare(faecher: Fach[]): string[] {
  return ersatzGruppen(faecher).map((g) => g.join(" ↔ "));
}

function ersatzGruppen(faecher: Fach[]): string[][] {
  const gruppen = new Map<string, string[]>();
  for (const f of faecher) {
    if (f.leer || f.platz === "Extern" || !f.kennung || !f.farbe) continue;
    const k = `${f.kennung}|${f.farbe.toUpperCase()}`;
    gruppen.set(k, [...(gruppen.get(k) ?? []), f.platz]);
  }
  return [...gruppen.values()].filter((g) => g.length > 1);
}

/** Wer für dieses Fach einspringt („A4"; mehrere mit Komma), "" = keiner. */
export function ersatzFuer(faecher: Fach[], platz: string): string {
  const g = ersatzGruppen(faecher).find((x) => x.includes(platz));
  return g ? g.filter((p) => p !== platz).join(", ") : "";
}
